import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Pagination,
  InputGroup,
  Card,
  Row,
  Col,
  Badge,
  Toast,
  ToastContainer,
  Tabs,
  Tab,
} from "react-bootstrap";
import AuthContext from "../contexts/AuthContext";
import api from "../api";
import MathJaxRenderer from "./MathJaxRenderer";
import { getFlaggedStatus, toggleFlaggedStatus } from "../api";
import LatestWorksheetsCarousel from "./LatestWorksheetsCarousel";

// Define the StarIcon component inline since there might be issues with the import
const StarIcon = ({ className, style }) => {
  return (
    <svg
      fill="currentColor"
      viewBox="0 0 576 512"
      className={className}
      style={{
        ...style,
        width: "1.2rem",
        height: "1.2rem",
        display: "inline-block",
        verticalAlign: "middle",
      }}
    >
      <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"></path>
    </svg>
  );
};

/**
 * @typedef {Object} Question
 * @property {string} id - Question ID
 * @property {string} tempId - Temporary ID for UI operations
 * @property {string} source - Source of the question ('system' or 'my_questions')
 * @property {string} question_body - Question content
 * @property {string} markscheme_body - Answer content
 * @property {string} subject - Subject name
 * @property {string} level - Question level
 * @property {string} [paper] - Optional paper number
 */

/**
 * DataList Component
 * Handles the display and management of questions, including:
 * - Question listing
 * - Quick worksheet generation
 * - Print functionality
 * - State management
 */
const DataList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [topicInput, setTopicInput] = useState("");
  const [filtersComplete, setFiltersComplete] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [showTopicSuggestions, setShowTopicSuggestions] = useState(false);
  const { auth } = useContext(AuthContext);
  const [content, setContent] = useState({});

  // Quick Worksheet Creator state
  const [showQuickWorksheet, setShowQuickWorksheet] = useState(false);
  const [quickWorksheetSubject, setQuickWorksheetSubject] = useState("");
  const [quickWorksheetLevel, setQuickWorksheetLevel] = useState("");
  const [quickWorksheetPaper, setQuickWorksheetPaper] = useState("");
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showGenerationSuccess, setShowGenerationSuccess] = useState(false);

  // Rumble Search state
  const [showRumbleSearch, setShowRumbleSearch] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState("");

  // Table selection state
  const [tableSelectedQuestions, setTableSelectedQuestions] = useState([]);
  const [showTableSelectionOptions, setShowTableSelectionOptions] =
    useState(false);

  // Check user permissions
  const canEdit =
    auth.user?.profile?.can_edit ||
    auth.user?.is_staff ||
    auth.user?.is_superuser;
  const canDelete =
    auth.user?.profile?.can_delete ||
    auth.user?.is_staff ||
    auth.user?.is_superuser;
  const canCreate =
    auth.user?.profile?.can_create ||
    auth.user?.is_staff ||
    auth.user?.is_superuser;

  // Check if user is view-only
  const isViewOnly =
    auth.isAuthenticated &&
    auth.user &&
    !auth.user.is_staff &&
    !auth.user.is_superuser &&
    auth.user.profile &&
    !auth.user.profile.can_create &&
    !auth.user.profile.can_edit &&
    !auth.user.profile.can_delete;

  // New state for My Questions integration
  const [includeMyQuestions, setIncludeMyQuestions] = useState(false);
  const [
    quickWorksheetIncludeMyQuestions,
    setQuickWorksheetIncludeMyQuestions,
  ] = useState(false); // For Thunder Generator

  // Reference to track timeouts for cleanup
  const timeoutRef = useRef(null);

  // Add state for lightning generator errors
  const [lightningError, setLightningError] = useState("");

  // New state for flagging
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [flaggingInProgress, setFlaggingInProgress] = useState({});
  const [flagToastMessage, setFlagToastMessage] = useState("");
  const [flagToastType, setFlagToastType] = useState("");
  const [showFlagToast, setShowFlagToast] = useState(false);

  /**
   * Manages the print operation lifecycle
   */
  const printStateRef = useRef({
    mounted: true,
    frame: null,
    timeouts: new Set(),
  });

  /**
   * Selects random questions from the available pool
   */
  const selectRandomQuestions = useCallback((questions) => {
    if (!questions || questions.length === 0) {
      return [];
    }

    // Determine how many questions to select (up to 5)
    const numToSelect = Math.min(5, questions.length);
    let selectedQuestions = [];

    if (questions.length <= numToSelect) {
      // If we have 5 or fewer questions, use all of them
      selectedQuestions = [...questions];
    } else {
      // Shuffle the array using Fisher-Yates algorithm
      const shuffled = [...questions];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      // Select the first numToSelect questions
      selectedQuestions = shuffled.slice(0, numToSelect);
    }

    // Add order metadata to questions
    return selectedQuestions.map((q, index) => ({
      ...q,
      selected_order: index + 1,
    }));
  }, []);

  // Fetch questions for worksheet
  const fetchQuestionsForWorksheet = useCallback(async () => {
    if (!quickWorksheetSubject) {
      return;
    }

    setLightningError("");
    setLoadingQuestions(true);
    setAvailableQuestions([]);
    setSelectedQuestions([]);

    try {
      const params = new URLSearchParams();
      params.append("subject", quickWorksheetSubject);

      if (quickWorksheetLevel) {
        params.append("level", quickWorksheetLevel);
      }

      if (quickWorksheetPaper) {
        params.append("paper", quickWorksheetPaper);
      }

      if (quickWorksheetIncludeMyQuestions) {
        params.append("include_my_questions", "true");
      }

      const response = await api.get(
        `/api/csv-data/worksheet/?${params.toString()}`
      );
      let systemQuestions = [];

      if (Array.isArray(response.data)) {
        systemQuestions = response.data;
      } else if (response.data && typeof response.data === "object") {
        if (Array.isArray(response.data.results)) {
          systemQuestions = response.data.results;
        } else {
          const extractedData = Object.values(response.data);
          systemQuestions = Array.isArray(extractedData) ? extractedData : [];
        }
      }

      if (systemQuestions.length === 0) {
        setLightningError(
          `No questions found for ${quickWorksheetSubject}. Please try another subject.`
        );
        setLoadingQuestions(false);
        return;
      }

      const processedQuestions = systemQuestions.map((q) => ({
        ...q,
        source: "system",
        tempId: `system-${q.id || Math.random().toString(36).substring(2, 10)}`,
      }));

      setAvailableQuestions(processedQuestions);

      if (processedQuestions.length > 0) {
        const selected = selectRandomQuestions(processedQuestions);
        setSelectedQuestions(selected.map((q) => q.tempId));
        setShowGenerationSuccess(true);
        setTimeout(() => {
          setShowGenerationSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Error fetching questions for worksheet:", error);
      setLightningError(
        `Error fetching worksheet questions: ${
          error.message || "Unknown error"
        }`
      );
    } finally {
      setLoadingQuestions(false);
    }
  }, [
    quickWorksheetSubject,
    quickWorksheetLevel,
    quickWorksheetPaper,
    quickWorksheetIncludeMyQuestions,
    selectRandomQuestions,
    setLightningError,
    setLoadingQuestions,
    setAvailableQuestions,
    setSelectedQuestions,
    setShowGenerationSuccess,
  ]);

  // Initialize print state ref
  useEffect(() => {
    printStateRef.current.mounted = true;
    return () => {
      printStateRef.current.mounted = false;
      // Clear any pending timeouts
      printStateRef.current.timeouts.forEach((timeout) =>
        clearTimeout(timeout)
      );
      printStateRef.current.timeouts.clear();
      // Remove any existing print frame
      if (
        printStateRef.current.frame &&
        printStateRef.current.frame.parentNode
      ) {
        printStateRef.current.frame.parentNode.removeChild(
          printStateRef.current.frame
        );
      }
    };
  }, []);

  useEffect(() => {
    if (filtersComplete && dataFetched) {
      fetchData();
    }
  }, [currentPage, filtersComplete, dataFetched]);

  useEffect(() => {
    fetchSubjects();
    fetchLevels();
  }, []);

  useEffect(() => {
    if (selectedSubject || selectedLevel || topicInput) {
      setDataFetched(false);
      setData([]);
    }
  }, [selectedSubject, selectedLevel, topicInput]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      if (quickWorksheetSubject && isSubscribed) {
        await fetchQuestionsForWorksheet();
      }
    };

    fetchData();

    return () => {
      isSubscribed = false;
    };
  }, [fetchQuestionsForWorksheet, quickWorksheetSubject]);

  useEffect(() => {
    // Reset data fetch state when filters change
    setDataFetched(false);

    // Check if required filters are populated (subject and level only now)
    if (selectedSubject && selectedLevel) {
      setFiltersComplete(true);
    } else {
      setFiltersComplete(false);
      // Clear data if filters are incomplete
      if (data.length > 0) {
        setData([]);
      }
    }
  }, [selectedSubject, selectedLevel]);

  const fetchData = async () => {
    if (!selectedSubject || !selectedLevel) {
      return;
    }

    setLoading(true);
    try {
      let endpoint = "/api/csv-data/";

      // Build query parameters
      let params = new URLSearchParams();
      params.append("subject", selectedSubject);
      params.append("level", selectedLevel);

      if (topicInput) {
        params.append("title", topicInput);
      }

      if (selectedPaper) {
        params.append("paper", selectedPaper);
      }

      // Add parameter for including user's questions
      if (includeMyQuestions) {
        params.append("include_my_questions", "true");
      }

      const response = await api.get(`${endpoint}?${params.toString()}`);

      // Extract data from response - handle both array and object formats
      let systemQuestions = [];
      if (Array.isArray(response.data)) {
        // If response.data is already an array
        systemQuestions = response.data;
      } else if (response.data && typeof response.data === "object") {
        // If it's an object with results property (common API pattern)
        if (Array.isArray(response.data.results)) {
          systemQuestions = response.data.results;
        } else {
          // Try to convert object to array if possible
          const extractedData = Object.values(response.data);
          systemQuestions = Array.isArray(extractedData) ? extractedData : [];
        }
      } else {
        systemQuestions = [];
      }

      // Mark all system questions
      systemQuestions = systemQuestions.map((q) => ({
        ...q,
        source: "system",
      }));

      if (includeMyQuestions) {
        // If including My Questions, also fetch user's questions and merge them
        try {
          const userQuestionsResponse = await api.get("/api/user-questions/");

          let userQuestions = [];
          if (Array.isArray(userQuestionsResponse.data)) {
            userQuestions = userQuestionsResponse.data;
          } else if (
            userQuestionsResponse.data &&
            typeof userQuestionsResponse.data === "object"
          ) {
            if (Array.isArray(userQuestionsResponse.data.results)) {
              userQuestions = userQuestionsResponse.data.results;
            } else {
              // Try to convert object to array if possible
              const extractedData = Object.values(userQuestionsResponse.data);
              userQuestions = Array.isArray(extractedData) ? extractedData : [];
            }
          }

          // Filter user questions to match current search criteria
          const filteredUserQuestions = userQuestions.filter((q) => {
            const matchesSubject = q.subject === selectedSubject;
            const matchesLevel = q.level === selectedLevel;
            const matchesPaper = !selectedPaper || q.paper === selectedPaper;
            const matchesTitle =
              !topicInput ||
              (q.title &&
                q.title.toLowerCase().includes(topicInput.toLowerCase()));

            return (
              matchesSubject && matchesLevel && matchesPaper && matchesTitle
            );
          });

          // Add a source property to identify user questions
          const markedUserQuestions = filteredUserQuestions.map((q) => ({
            ...q,
            source: "my_questions",
          }));

          // Combine both sets of questions
          setData([...systemQuestions, ...markedUserQuestions]);
        } catch (error) {
          console.error("Error fetching user questions:", error);
          // If user questions fetch fails, still show system questions
          setData(systemQuestions);
        }
      } else {
        // Just use system questions
        setData(systemQuestions);
      }

      setDataFetched(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await api.get("/api/csv-data/subjects/");
      if (response.data && Array.isArray(response.data)) {
        console.log("Available subjects:", response.data);

        // Log whether "Test Mathematics AA HL" is in the list
        const testSubject = "Test Mathematics AA HL";
        const hasTestSubject = response.data.includes(testSubject);
        console.log(
          `Subject "${testSubject}" found in subjects list: ${hasTestSubject}`
        );

        // Log subjects that might be similar to "Test Mathematics AA HL"
        const similarSubjects = response.data.filter(
          (subject) =>
            subject.toLowerCase().includes("mathematics") ||
            subject.toLowerCase().includes("test") ||
            subject.toLowerCase().includes("hl")
        );
        console.log(
          `Similar subjects found: ${similarSubjects.length}`,
          similarSubjects
        );

        setSubjects(response.data);
      }
    } catch (err) {
      console.error("Error fetching subjects:", err);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchLevels = async () => {
    try {
      const response = await api.get("/api/csv-data/levels/");
      if (response.data && Array.isArray(response.data)) {
        console.log("Available levels:", response.data);
        setLevels(response.data);
      }
    } catch (err) {
      console.error("Error fetching levels:", err);
      // If the levels endpoint is not available, extract levels from the data
      try {
        console.log("Levels endpoint not available, extracting from data");
        const dataResponse = await api.get("/api/csv-data/?limit=1000");
        if (dataResponse.data && dataResponse.data.results) {
          // Extract unique levels from data
          const uniqueLevels = [
            ...new Set(
              dataResponse.data.results
                .map((item) => item.level)
                .filter((level) => level && level.trim() !== "")
            ),
          ].sort();

          console.log("Extracted levels:", uniqueLevels);
          setLevels(uniqueLevels);
        }
      } catch (extractErr) {
        console.error("Error extracting levels from data:", extractErr);
        // Set some default levels as fallback
        setLevels(["HL", "SL", "Core", "Extended"]);
      }
    }
  };

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!selectedSubject || !selectedLevel) {
      return;
    }

    setFiltersComplete(true);

    // Fetch data with current filters and includeMyQuestions state
    fetchData();

    // Reset table selections when performing a new search
    setTableSelectedQuestions([]);
  };

  const handleSubjectChange = (e) => {
    const subject = e.target.value;
    console.log("Selected subject:", subject);
    setSelectedSubject(subject);
    setSelectedLevel("");
    setTopicInput("");
    setSelectedPaper("");
    setCurrentPage(1);
    setData([]);
    setDataFetched(false);
  };

  const handleLevelChange = (e) => {
    if (!selectedSubject) {
      setError("Please select a subject first");
      return;
    }

    const level = e.target.value;
    console.log("Selected level:", level);
    setSelectedLevel(level);
    // Don't reset the topic when changing level to allow for refining searches
    // setTopicInput("");
    setCurrentPage(1);
    setData([]);
    setDataFetched(false);
  };

  const handleTopicChange = (e) => {
    setTopicInput(e.target.value);
    // We'll let the Search button trigger the fetch instead of doing it on every keystroke
    // This prevents excessive API calls and infinite loops
  };

  const handlePaperChange = (e) => {
    const paper = e.target.value;
    console.log("Selected paper:", paper);
    setSelectedPaper(paper);
    setCurrentPage(1);
    setData([]);
    setDataFetched(false);
  };

  const clearFilters = () => {
    setSelectedSubject("");
    setSelectedLevel("");
    setTopicInput("");
    setSelectedPaper("");
    setIncludeMyQuestions(false);
    setData([]);
    setDataFetched(false);
    setFiltersComplete(false);
    setTableSelectedQuestions([]);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await api.delete(`/api/csv-data/${id}/`);
        // Refresh the current page after deletion
        fetchData();
      } catch (err) {
        console.error("Error deleting record:", err);
        if (err.response?.status === 403) {
          setError("You don't have permission to delete records.");
        } else {
          setError("Failed to delete record. Please try again.");
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord({ ...currentRecord, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const response = await api.put(
        `/api/csv-data/${currentRecord.id}/`,
        currentRecord
      );
      setData(
        data.map((item) =>
          item.id === currentRecord.id ? response.data : item
        )
      );
      setShowModal(false);
    } catch (err) {
      console.error("Error updating record:", err);
      if (err.response?.status === 403) {
        setError("You don't have permission to edit records.");
      } else {
        setError("Failed to update record. Please try again.");
      }
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Helper function to create a preview of HTML content
  const createHTMLPreview = (htmlContent, maxLength = 200) => {
    if (!htmlContent) return "-";

    // Create a temporary DOM element to strip HTML tags for the preview
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent || "";
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // Return truncated text for preview
    return textContent.length > maxLength
      ? `${textContent.substring(0, maxLength)}...`
      : textContent;
  };

  // Function to safely render HTML content
  const renderHTML = (htmlContent) => {
    if (!htmlContent) return <em>No content available</em>;
    const safeContent = htmlContent || "";
    return <div dangerouslySetInnerHTML={{ __html: safeContent }} />;
  };

  const renderQuestion = (question) => {
    if (!question) return null;
    const safeQuestion = {
      title: question.title || "Untitled Question",
      subject: question.subject || "",
      level: question.level || "",
      paper: question.paper || "",
      question_number: question.question_number || "",
      question_body: question.question_body || "",
      markscheme_body: question.markscheme_body || "",
      marks: question.marks || "",
    };

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5>{safeQuestion.title}</h5>
          <Badge bg="info">
            {safeQuestion.marks ? `${safeQuestion.marks} marks` : ""}
          </Badge>
        </div>
        <p>
          <strong>Subject:</strong> {safeQuestion.subject},{" "}
          <strong>Level:</strong> {safeQuestion.level}
          {safeQuestion.paper && (
            <>
              , <strong>Paper:</strong> {safeQuestion.paper}
            </>
          )}
          {safeQuestion.question_number && (
            <>
              , <strong>Question:</strong> {safeQuestion.question_number}
            </>
          )}
        </p>
        <Tabs
          defaultActiveKey="question"
          id="question-content-tabs"
          className="mb-3"
        >
          <Tab eventKey="question" title="Question">
            <div className="border p-3 bg-light rounded">
              <MathJaxRenderer content={safeQuestion.question_body} />
            </div>
          </Tab>
          <Tab eventKey="question_source" title="Question HTML">
            <pre
              className="border p-3 bg-light rounded"
              style={{ maxHeight: "300px", overflow: "auto" }}
            >
              {safeQuestion.question_body}
            </pre>
          </Tab>
          <Tab eventKey="markscheme" title="Markscheme">
            <div className="border p-3 bg-light rounded">
              <MathJaxRenderer content={safeQuestion.markscheme_body} />
            </div>
          </Tab>
          <Tab eventKey="markscheme_source" title="Markscheme HTML">
            <pre
              className="border p-3 bg-light rounded"
              style={{ maxHeight: "300px", overflow: "auto" }}
            >
              {safeQuestion.markscheme_body}
            </pre>
          </Tab>
        </Tabs>
      </div>
    );
  };

  /**
   * Regenerates the worksheet with new random questions
   */
  const regenerateQuestions = () => {
    setRegenerating(true);
    // Select a new set of random questions from available questions
    const newSelected = selectRandomQuestions(availableQuestions);
    setSelectedQuestions(newSelected.map((q) => q.tempId));

    // Show success message
    setShowGenerationSuccess(true);
    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowGenerationSuccess(false);
    }, 3000);

    setRegenerating(false);
  };

  // Replace with a simple direct print function
  const handlePrint = useCallback(() => {
    try {
      const questionsToDisplay = availableQuestions.filter((q) =>
        selectedQuestions.includes(q.tempId)
      );

      if (questionsToDisplay.length === 0) {
        alert("No questions available to print");
        return;
      }

      const content = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Quick Worksheet: ${
            quickWorksheetSubject || "Lightning Worksheet"
          }</title>
          <style>
            body {
              font-family: 'Times New Roman', Times, serif;
              line-height: 1.6;
              margin: 20mm;
              font-size: 12pt;
            }
            .page-break-after { page-break-after: always; }
            .answer-section { page-break-before: always; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 30px;">
            <h2>Quick Worksheet: ${
              quickWorksheetSubject || "Lightning Worksheet"
            }</h2>
            ${quickWorksheetLevel ? `<p>Level: ${quickWorksheetLevel}</p>` : ""}
          </div>
          
          ${questionsToDisplay
            .map(
              (question, index) => `
            <div class="page-break-after">
              <h4>Question ${index + 1}${
                question.source === "my_questions"
                  ? ' <span style="display: inline-block; font-size: 10pt; padding: 2px 6px; border-radius: 4px; background-color: #cfe2ff; color: #0a58ca; margin-left: 8px;">My Question</span>'
                  : ""
              }</h4>
              <div>${question.question_body || ""}</div>
            </div>
          `
            )
            .join("")}
          
          <div class="answer-section">
            <h2 style="text-align: center; margin-bottom: 30px;">Answer Key</h2>
            ${questionsToDisplay
              .map(
                (question, index) => `
              <div style="margin-bottom: 30px;">
                <h4>Answer ${index + 1}${
                  question.source === "my_questions"
                    ? ' <span style="display: inline-block; font-size: 10pt; padding: 2px 6px; border-radius: 4px; background-color: #cfe2ff; color: #0a58ca; margin-left: 8px;">My Question</span>'
                    : ""
                }</h4>
                <div>${question.markscheme_body || ""}</div>
              </div>
            `
              )
              .join("")}
          </div>
        </body>
        </html>
      `;

      // Create and download the file
      const blob = new Blob([content], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Worksheet_${
        quickWorksheetSubject || "Lightning"
      }_${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error("Error creating worksheet:", error);
      alert("Failed to create worksheet. Please try again.");
    }
  }, [
    availableQuestions,
    selectedQuestions,
    quickWorksheetSubject,
    quickWorksheetLevel,
  ]);

  // Remove the preview button click handler since we're not using the modal anymore
  const handlePreview = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  // Handle table row selection
  const handleTableRowSelect = (record) => {
    setTableSelectedQuestions((prev) => {
      // If already selected, remove it
      if (prev.some((q) => q.id === record.id)) {
        return prev.filter((q) => q.id !== record.id);
      }

      // If trying to add more than 5, show error
      if (prev.length >= 5) {
        setError("You can only select up to 5 questions for a worksheet.");
        return prev;
      }

      // Add to selection
      return [...prev, record];
    });

    // Show the print options if at least one question is selected
    if (!tableSelectedQuestions.some((q) => q.id === record.id)) {
      setShowTableSelectionOptions(true);
    } else if (tableSelectedQuestions.length <= 1) {
      // If we're removing the last or second-to-last question, hide options
      setShowTableSelectionOptions(tableSelectedQuestions.length > 1);
    }
  };

  // Clear table selections
  const clearTableSelections = () => {
    setTableSelectedQuestions([]);
    setShowTableSelectionOptions(false);
  };

  // Print selected questions from table
  const printSelectedFromTable = () => {
    if (tableSelectedQuestions.length === 0) {
      return;
    }

    // Create a clean printable content with minimal styling
    const subject = selectedSubject || "Various Subjects";
    const level = selectedLevel || "Various Levels";
    const questionCount = tableSelectedQuestions.length;

    // Sort questions by their selection order
    const sortedQuestions = [...tableSelectedQuestions].sort(
      (a, b) => a.selectionOrder - b.selectionOrder
    );

    const printableContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Custom Worksheet - ${subject} (${level})</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12pt;
          }
          .question {
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          .source-badge {
            display: inline-block;
            font-size: 10pt;
            padding: 2px 6px;
            border-radius: 4px;
            background-color: #e9e9e9;
            margin-left: 8px;
          }
          .my-question-badge {
            background-color: #cfe2ff;
            color: #0a58ca;
          }
          h1, h2, h3, h4 {
            margin-top: 10px;
            margin-bottom: 10px;
          }
          .header {
            margin-bottom: 20px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 10px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10pt;
            color: #666;
          }
          .solution-page {
            page-break-before: always;
          }
          @media print {
            @page {
              margin: 1.5cm;
            }
          }
        </style>
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </head>
      <body>
        <div class="header">
          <h1>${subject} - ${level}</h1>
          <p>Custom worksheet with ${questionCount} selected questions</p>
          <p>Generated: ${new Date().toLocaleDateString()}</p>
        </div>

        <h2>Questions</h2>
        ${sortedQuestions
          .map(
            (question, index) => `
            <div class="question">
              <h3>Question ${index + 1}: ${question.title || "Untitled"} 
                ${
                  question.source === "my_questions"
                    ? '<span class="source-badge my-question-badge">My Question</span>'
                    : ""
                }
              </h3>
              <div>${question.question_body || ""}</div>
            </div>
            `
          )
          .join("")}

        <div class="solution-page">
          <h2>Mark Scheme</h2>
          ${sortedQuestions
            .map(
              (question, index) => `
              <div class="question">
                <h3>Answer ${index + 1}: ${question.title || "Untitled"}
                  ${
                    question.source === "my_questions"
                      ? '<span class="source-badge my-question-badge">My Question</span>'
                      : ""
                  }
                </h3>
                <div>${question.markscheme_body || ""}</div>
              </div>
              `
            )
            .join("")}
        </div>

        <div class="footer">
          <p>Worksheet generated from the Thunder Generator system</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printableContent);
    printWindow.document.close();
  };

  // Download selected table questions as HTML
  const downloadSelectedAsHTML = () => {
    if (tableSelectedQuestions.length === 0) {
      return;
    }

    // Sort questions by their selection order
    const sortedQuestions = [...tableSelectedQuestions].sort(
      (a, b) => a.selectionOrder - b.selectionOrder
    );

    const subject = selectedSubject || "Various Subjects";
    const level = selectedLevel || "Various Levels";
    const questionCount = sortedQuestions.length;
    const fileName = `${subject.replace(/\s+/g, "-")}_${level.replace(
      /\s+/g,
      "-"
    )}_worksheet.html`;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Custom Worksheet - ${subject} (${level})</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12pt;
          }
          .question {
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          .source-badge {
            display: inline-block;
            font-size: 10pt;
            padding: 2px 6px;
            border-radius: 4px;
            background-color: #e9e9e9;
            margin-left: 8px;
          }
          .my-question-badge {
            background-color: #cfe2ff;
            color: #0a58ca;
          }
          h1, h2, h3, h4 {
            margin-top: 10px;
            margin-bottom: 10px;
          }
          .header {
            margin-bottom: 20px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 10px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10pt;
            color: #666;
          }
          .solution-page {
            page-break-before: always;
          }
          @media print {
            @page {
              margin: 1.5cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${subject} - ${level}</h1>
          <p>Custom worksheet with ${questionCount} selected questions</p>
          <p>Generated: ${new Date().toLocaleDateString()}</p>
        </div>

        <h2>Questions</h2>
        ${sortedQuestions
          .map(
            (question, index) => `
            <div class="question">
              <h3>Question ${index + 1}: ${question.title || "Untitled"} 
                ${
                  question.source === "my_questions"
                    ? '<span class="source-badge my-question-badge">My Question</span>'
                    : ""
                }
              </h3>
              <div>${question.question_body || ""}</div>
            </div>
            `
          )
          .join("")}

        <div class="solution-page">
          <h2>Mark Scheme</h2>
          ${sortedQuestions
            .map(
              (question, index) => `
              <div class="question">
                <h3>Answer ${index + 1}: ${question.title || "Untitled"}
                  ${
                    question.source === "my_questions"
                      ? '<span class="source-badge my-question-badge">My Question</span>'
                      : ""
                  }
                </h3>
                <div>${question.markscheme_body || ""}</div>
              </div>
              `
            )
            .join("")}
        </div>

        <div class="footer">
          <p>Worksheet generated from the Thunder Generator system</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob and download link
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 100);
  };

  /**
   * Simplified flag toggle handler using direct API calls
   * Uses optimistic updates for responsive UI
   */
  const handleToggleFlag = async (type, id) => {
    const key = `${type}_${id}`;

    // Set loading state immediately for better UX
    setFlaggingInProgress((prev) => ({ ...prev, [key]: true }));

    try {
      // Get current state from our state
      const currentState = flaggedQuestions[key] || false;
      const newState = !currentState;

      console.log(
        `Toggling flag for ${key} from ${currentState} to ${newState}`
      );

      // Optimistically update UI immediately
      setFlaggedQuestions((prev) => ({
        ...prev,
        [key]: newState,
      }));

      // Also update localStorage as an immediate fallback
      try {
        const storedFlags = JSON.parse(
          localStorage.getItem("flaggedQuestions") || "{}"
        );
        storedFlags[key] = newState;
        localStorage.setItem("flaggedQuestions", JSON.stringify(storedFlags));
      } catch (storageError) {
        console.warn("Could not update localStorage:", storageError);
      }

      // Show success toast
      setFlagToastMessage(
        newState
          ? "Question added to flagged questions"
          : "Question removed from flagged questions"
      );
      setFlagToastType("success");
      setShowFlagToast(true);

      // Call the API
      const payload = {};
      if (type === "system") {
        payload.system_question_id = id;
      } else {
        payload.user_question_id = id;
      }

      const response = await api.post(
        "/api/flagged-questions/toggle_flag/",
        payload
      );
      const apiNewState = response.data.is_flagged;

      // If API response differs from our optimistic update, revert
      if (apiNewState !== newState) {
        setFlaggedQuestions((prev) => ({
          ...prev,
          [key]: apiNewState,
        }));

        // Update localStorage to match API
        try {
          const storedFlags = JSON.parse(
            localStorage.getItem("flaggedQuestions") || "{}"
          );
          storedFlags[key] = apiNewState;
          localStorage.setItem("flaggedQuestions", JSON.stringify(storedFlags));
        } catch (storageError) {
          console.warn("Could not update localStorage:", storageError);
        }
      }

      // Dispatch event to notify other components
      window.dispatchEvent(new Event("flaggedQuestionsUpdated"));
    } catch (error) {
      console.error("Error toggling flag:", error);

      // Revert optimistic update on error
      const originalState = flaggedQuestions[key] || false;
      setFlaggedQuestions((prev) => ({
        ...prev,
        [key]: originalState,
      }));

      // Show error toast
      setFlagToastMessage("Failed to update flagged status");
      setFlagToastType("error");
      setShowFlagToast(true);
    } finally {
      setFlaggingInProgress((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Add useEffect to load initial flag states
  useEffect(() => {
    // Load from localStorage first for immediate display
    try {
      const storedFlags = JSON.parse(
        localStorage.getItem("flaggedQuestions") || "{}"
      );
      setFlaggedQuestions(storedFlags);
    } catch (error) {
      console.warn("Could not load flags from localStorage:", error);
    }

    // Add event listener for flag updates from other components
    const handleFlagUpdate = () => {
      try {
        const storedFlags = JSON.parse(
          localStorage.getItem("flaggedQuestions") || "{}"
        );
        setFlaggedQuestions(storedFlags);
      } catch (error) {
        console.warn("Could not load flags from localStorage:", error);
      }
    };

    window.addEventListener("flaggedQuestionsUpdated", handleFlagUpdate);

    return () => {
      window.removeEventListener("flaggedQuestionsUpdated", handleFlagUpdate);
    };
  }, []);

  // Add this effect to load flag status for visible questions when component mounts or data changes
  useEffect(() => {
    // Load flag status for visible questions
    const checkFlagStatusForVisibleQuestions = async () => {
      if (data.length === 0) return;

      // Use a batch size to avoid too many concurrent API calls
      const BATCH_SIZE = 10;
      const batches = [];

      // Split questions into batches
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        batches.push(data.slice(i, i + BATCH_SIZE));
      }

      // Process batches sequentially to avoid overwhelming the API
      for (const batch of batches) {
        const statusPromises = batch.map(async (question) => {
          const questionType = question.source || "system";
          const questionId = question.id || question.system_id;
          const key = `${questionType}_${questionId}`;

          try {
            const isFlagged = await checkQuestionFlagStatus(
              questionType,
              questionId
            );
            return { key, isFlagged };
          } catch (error) {
            console.error(`Error checking flag status for ${key}:`, error);
            return { key, isFlagged: false };
          }
        });

        const results = await Promise.all(statusPromises);

        const updatedFlags = {};
        results.forEach(({ key, isFlagged }) => {
          updatedFlags[key] = isFlagged;
        });

        setFlaggedQuestions((prev) => ({
          ...prev,
          ...updatedFlags,
        }));

        // Small delay between batches to avoid API rate limits
        if (batches.length > 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    };

    checkFlagStatusForVisibleQuestions();
  }, [data]);

  // Simplified check flag status function using our new API method
  const checkQuestionFlagStatus = async (questionType, id) => {
    const key = `${questionType}_${id}`;

    try {
      // First check localStorage for an immediate response
      try {
        const storedFlags = JSON.parse(
          localStorage.getItem("flaggedQuestions") || "{}"
        );
        const localState = storedFlags[key];

        if (localState !== undefined) {
          console.log(
            `Using localStorage flag status for ${key}: ${localState}`
          );
          return !!localState; // Convert to boolean
        }
      } catch (storageError) {
        console.warn(
          `Error reading flag status from localStorage:`,
          storageError
        );
      }

      // Get flag status directly from our state if available
      if (flaggedQuestions[key] !== undefined) {
        return flaggedQuestions[key];
      }

      // Otherwise, try to get from API
      const apiResult = await getFlaggedStatus(questionType, id);

      // Store in state and localStorage for future use
      setFlaggedQuestions((prev) => ({
        ...prev,
        [key]: apiResult,
      }));

      try {
        const storedFlags = JSON.parse(
          localStorage.getItem("flaggedQuestions") || "{}"
        );
        storedFlags[key] = apiResult;
        localStorage.setItem("flaggedQuestions", JSON.stringify(storedFlags));
      } catch (storageError) {
        console.warn(`Error saving flag status to localStorage:`, storageError);
      }

      return apiResult;
    } catch (error) {
      console.error(`Error checking flag status for ${key}:`, error);
      // In case of error, default to not flagged
      return false;
    }
  };

  // Update the cleanup functions to remove print-related state references
  useEffect(() => {
    return () => {
      // Cleanup for other resources if needed
    };
  }, []);

  // Update the modal close handlers to remove print-related state references
  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {/* Lightning and Thunder buttons - enhanced prominent section */}
      <Card className="mb-4 border-0 shadow">
        <Card.Body className="p-4">
          <Row>
            <Col md={4} className="mb-3 mb-md-0">
              <div className="d-flex flex-column h-100 p-4 rounded lightning-card">
                <h5 className="mb-3">
                  <i className="bi bi-lightning-fill text-warning me-2"></i>
                  Lightning
                </h5>
                <div className="flex-grow-1">
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Generate instantly</strong> - Create worksheets
                      with 5 random questions
                    </li>
                    <li>
                      <strong>Print-ready</strong> - Download and print
                      worksheets immediately
                    </li>
                    <li>
                      <strong>Select subject</strong> - Customize by your
                      teaching area
                    </li>
                  </ul>
                </div>
                <Button
                  variant="warning"
                  className="fw-bold mt-3 btn-lightning"
                  size="lg"
                  onClick={() => setShowQuickWorksheet(!showQuickWorksheet)}
                >
                  <i className="bi bi-lightning-fill me-2"></i>
                  {showQuickWorksheet
                    ? "Hide Lightning Generator"
                    : "Lightning Generator"}
                </Button>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex flex-column h-100 p-4 rounded thunder-card">
                <h5 className="mb-3">
                  <i className="bi bi-cloud-lightning-fill text-primary me-2"></i>
                  Thunder
                </h5>
                <div className="flex-grow-1">
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Advanced customization</strong> - Fine-tune your
                      worksheets
                    </li>
                    <li>
                      <strong>Professional printing</strong> - Create
                      high-quality printable materials
                    </li>
                    <li>
                      <strong>Complete control</strong> - Hand-pick exact
                      questions for your needs
                    </li>
                  </ul>
                </div>
                <Button
                  variant="primary"
                  className="fw-bold mt-3 btn-thunder"
                  size="lg"
                  onClick={() => navigate("/worksheets")}
                >
                  <i className="bi bi-cloud-lightning-fill me-2"></i>
                  Thunder Generator
                </Button>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex flex-column h-100 p-4 rounded rumble-card">
                <h5 className="mb-3">
                  <i className="bi bi-search text-success me-2"></i>
                  Rumble
                </h5>
                <div className="flex-grow-1">
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Powerful search</strong> - Find exactly what you
                      need
                    </li>
                    <li>
                      <strong>Flag questions</strong> - Save favorites for
                      future reference
                    </li>
                    <li>
                      <strong>Quick filtering</strong> - Sort by subject, level,
                      and more
                    </li>
                  </ul>
                </div>
                <Button
                  variant="success"
                  className="fw-bold mt-3 btn-rumble"
                  size="lg"
                  onClick={() => setShowRumbleSearch(!showRumbleSearch)}
                >
                  <i className="bi bi-search me-2"></i>
                  {showRumbleSearch ? "Hide Rumble Search" : "Rumble Search"}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Quick Worksheet Creator */}
      {showQuickWorksheet && (
        <Card className="mb-4">
          <Card.Header className="lightning-header">
            <h4>
              <i className="bi bi-lightning-fill me-1"></i>Lightning Worksheet
              Creator
            </h4>
          </Card.Header>
          <Card.Body>
            {lightningError && (
              <Alert
                variant="danger"
                onClose={() => setLightningError("")}
                dismissible
              >
                {lightningError}
              </Alert>
            )}

            <p>
              Select a subject to instantly generate a worksheet with 5 random
              questions at lightning speed.
            </p>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Subject</Form.Label>
                  <Form.Select
                    value={quickWorksheetSubject}
                    onChange={(e) => {
                      setQuickWorksheetSubject(e.target.value);
                      setSelectedQuestions([]);
                      if (e.target.value) {
                        // Automatically fetch questions when subject is selected
                        setTimeout(() => {
                          fetchQuestionsForWorksheet();
                        }, 100);
                      }
                    }}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((sub, index) => (
                      <option key={index} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Level (Optional)</Form.Label>
                  <Form.Select
                    value={quickWorksheetLevel}
                    onChange={(e) => {
                      setQuickWorksheetLevel(e.target.value);
                      setSelectedQuestions([]);
                    }}
                  >
                    <option value="">Any Level</option>
                    {levels.map((level, index) => (
                      <option key={index} value={level}>
                        {level}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Paper (Optional)</Form.Label>
                  <Form.Select
                    value={quickWorksheetPaper}
                    onChange={(e) => {
                      setQuickWorksheetPaper(e.target.value);
                      setSelectedQuestions([]);
                    }}
                  >
                    <option value="">Any Paper</option>
                    <option value="1">Paper 1</option>
                    <option value="2">Paper 2</option>
                    <option value="3">Paper 3</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="quick-worksheet-include-my-questions"
                    label="Include questions from My Questions"
                    checked={quickWorksheetIncludeMyQuestions}
                    onChange={(e) =>
                      setQuickWorksheetIncludeMyQuestions(e.target.checked)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {loadingQuestions ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading questions...</p>
              </div>
            ) : quickWorksheetSubject && availableQuestions.length > 0 ? (
              <div>
                {selectedQuestions.length > 0 ? (
                  <div className="p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Your Worksheet</h5>
                      <Badge bg="primary">
                        {selectedQuestions.length} Questions Selected
                      </Badge>
                    </div>
                    <p className="text-muted">
                      A worksheet with {selectedQuestions.length} random
                      questions has been generated
                      {quickWorksheetLevel
                        ? ` for level "${quickWorksheetLevel}"`
                        : ""}
                      {quickWorksheetPaper
                        ? ` from Paper ${quickWorksheetPaper}`
                        : ""}
                      . Click "Download Worksheet" to save and print the
                      worksheet, or "Regenerate" for a new set of questions.
                    </p>
                    {regenerating && (
                      <div className="mt-2 alert alert-info py-2">
                        <div className="d-flex align-items-center">
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          <span>
                            Regenerating your worksheet with new questions...
                          </span>
                        </div>
                      </div>
                    )}
                    {showGenerationSuccess && (
                      <div className="mt-2 alert alert-success py-2">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            Success! Your worksheet has been generated.
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-column">
                      <Button
                        variant="warning"
                        onClick={regenerateQuestions}
                        disabled={regenerating}
                        className="mb-2"
                        size="md"
                        style={{
                          background:
                            "linear-gradient(to right, #f9ca24, #f0932b)",
                          color: "#333",
                          border: "none",
                          padding: "8px 16px",
                        }}
                      >
                        <i className="bi bi-lightning-fill me-1"></i>
                        Generate New Lightning Set
                      </Button>

                      <Button
                        variant="primary"
                        onClick={handlePrint}
                        disabled={regenerating}
                        className="mt-2"
                        size="md"
                        style={{
                          padding: "8px 16px",
                        }}
                      >
                        <i className="bi bi-download me-1"></i>
                        Download Worksheet
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Alert variant="info">
                    No questions have been selected. Please check if the subject
                    has available questions.
                  </Alert>
                )}
              </div>
            ) : quickWorksheetSubject ? (
              <Alert variant="info">
                No questions found for this subject
                {quickWorksheetLevel ? ` and level` : ""}. Please try another
                selection.
              </Alert>
            ) : (
              <Alert variant="info">
                Please select a subject to generate a worksheet.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Search and filtering area - modified to enforce a sequence */}
      {showRumbleSearch && (
        <Card className="mb-4">
          <Card.Header className="thunder-header">
            <h4>
              <i className="bi bi-search me-2"></i>Rumble
            </h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info" className="mb-3">
              <div>
                <strong>Usage Instructions:</strong>
                <ol className="mb-0 mt-1">
                  <li>First, select a subject</li>
                  <li>Then, select a level</li>
                  <li>
                    Optionally, enter keywords to search in question titles
                  </li>
                  <li>Optionally, select a specific paper (1, 2, or 3)</li>
                  <li>Click "Search" to display matching questions</li>
                </ol>
              </div>
            </Alert>

            <Alert variant="info" className="mb-3">
              <strong>New feature:</strong> You can now manually select up to 5
              questions from the search results by using the checkboxes in the
              table. Once selected, you can print or download them as a custom
              worksheet.
            </Alert>

            <Form onSubmit={handleSearch}>
              <Row className="align-items-end">
                <Col md={3}>
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>
                      1. Select Subject <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      required
                    >
                      <option value="">Select a Subject</option>
                      {subjects.map((subject, index) => (
                        <option key={index} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>
                      2. Select Level <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedLevel}
                      onChange={handleLevelChange}
                      disabled={!selectedSubject}
                      required
                    >
                      <option value="">Select a Level</option>
                      {levels.map((level, index) => (
                        <option key={index} value={level}>
                          {level}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>3. Enter Title Search Terms</Form.Label>
                    <Form.Control
                      placeholder="Enter keywords to search in question titles..."
                      value={topicInput}
                      onChange={handleTopicChange}
                      disabled={!selectedSubject || !selectedLevel}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>4. Select Paper (Optional)</Form.Label>
                    <Form.Select
                      value={selectedPaper}
                      onChange={handlePaperChange}
                      disabled={!selectedSubject || !selectedLevel}
                    >
                      <option value="">Any Paper</option>
                      <option value="1">Paper 1</option>
                      <option value="2">Paper 2</option>
                      <option value="3">Paper 3</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="include-my-questions"
                      label="Include questions from My Questions"
                      checked={includeMyQuestions}
                      onChange={(e) => setIncludeMyQuestions(e.target.checked)}
                      disabled={!selectedSubject || !selectedLevel}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                </Col>
                <Col md={2}>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!selectedSubject || !selectedLevel}
                    >
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Active filters display */}
      {(selectedSubject || selectedLevel || topicInput || selectedPaper) && (
        <div className="mb-3">
          <strong>Active Filters:</strong>
          {selectedSubject && (
            <Badge bg="primary" className="me-2">
              Subject: {selectedSubject}
            </Badge>
          )}
          {selectedLevel && (
            <Badge bg="secondary" className="me-2">
              Level: {selectedLevel}
            </Badge>
          )}
          {topicInput && (
            <Badge bg="info" className="me-2">
              Title: {topicInput}
            </Badge>
          )}
          {selectedPaper && (
            <Badge bg="warning" className="me-2">
              Paper: {selectedPaper}
            </Badge>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading data...</p>
        </div>
      ) : filtersComplete && dataFetched ? (
        <>
          {data.length === 0 ? (
            <Card className="text-center p-4">
              <Card.Body>
                <Card.Title>No Results Found</Card.Title>
                <Card.Text>
                  No questions match your search criteria. Try different filters
                  or a different topic.
                  {selectedPaper && (
                    <span>
                      {" "}
                      You can try selecting "Any Paper" instead of Paper{" "}
                      {selectedPaper}.
                    </span>
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          ) : (
            <>
              <p className="mb-3">
                <small className="text-muted">
                  Showing {data.length} questions for {selectedSubject} (
                  {selectedLevel})
                  {topicInput ? ` related to "${topicInput}"` : ""}
                  {selectedPaper ? ` from Paper ${selectedPaper}` : ""}
                </small>
              </p>

              {/* Selected table questions options - MOVED HERE to appear above the data table */}
              {showTableSelectionOptions && (
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Selected Questions</h5>
                    <Badge bg="primary">
                      {tableSelectedQuestions.length}/5 Selected
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col md={7}>
                        <p className="mb-0">
                          Select up to 5 questions from the table below by
                          clicking the checkboxes. Once selected, you can print
                          or download them as a custom worksheet.
                        </p>
                      </Col>
                      <Col md={5}>
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={printSelectedFromTable}
                            disabled={tableSelectedQuestions.length === 0}
                            className="px-3 py-2"
                          >
                            <i className="bi bi-printer me-1"></i> Print
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={downloadSelectedAsHTML}
                            disabled={tableSelectedQuestions.length === 0}
                            className="px-3 py-2"
                          >
                            <i className="bi bi-download me-1"></i> Download
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={clearTableSelections}
                            className="px-3 py-2"
                          >
                            <i className="bi bi-x-circle me-1"></i> Clear
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Instruction for table selection */}
              <Alert variant="info" className="mb-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <span>
                    <strong>Select up to 5 questions</strong> from the table
                    below to create a custom worksheet.
                  </span>
                </div>
              </Alert>

              <div className="table-responsive data-table">
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th width="5%" className="text-center">
                        Select
                      </th>
                      <th width="5%" className="text-center">
                        Flag
                      </th>
                      <th width="15%">Question Title</th>
                      <th width="10%">Subject</th>
                      <th width="27%">Question</th>
                      <th width="28%">Mark Scheme</th>
                      {(canEdit || canDelete) && <th width="10%">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item) => {
                      const questionId = item.id || item.system_id;
                      const questionType = item.source || "system";
                      const flagKey = `${questionType}_${questionId}`;

                      return (
                        <tr
                          key={questionId}
                          className={
                            tableSelectedQuestions.some((q) => q.id === item.id)
                              ? "table-primary"
                              : ""
                          }
                        >
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              onChange={() => handleTableRowSelect(item)}
                              checked={tableSelectedQuestions.some(
                                (q) => q.id === item.id
                              )}
                              disabled={
                                tableSelectedQuestions.length >= 5 &&
                                !tableSelectedQuestions.some(
                                  (q) => q.id === item.id
                                )
                              }
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-warning"
                              onClick={() =>
                                handleToggleFlag(questionType, questionId)
                              }
                              disabled={flaggingInProgress[flagKey]}
                              title={
                                flaggedQuestions[flagKey]
                                  ? "Unflag question"
                                  : "Flag question"
                              }
                              style={{
                                minWidth: "30px",
                                minHeight: "30px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "none",
                                background: "transparent",
                              }}
                            >
                              {flaggingInProgress[flagKey] ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                <StarIcon
                                  className={
                                    flaggedQuestions[flagKey]
                                      ? "text-warning"
                                      : "text-muted"
                                  }
                                  style={{ fontSize: "1.2rem" }}
                                />
                              )}
                            </Button>
                          </td>
                          <td>{item.title}</td>
                          <td>{item.subject}</td>
                          <td>
                            <div className="question-body-preview">
                              {createHTMLPreview(item.question_body, 200)}
                              {item.question_body &&
                                item.question_body.length > 200 && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleEdit(item)}
                                    className="p-0 ms-1"
                                  >
                                    View Full Question
                                  </Button>
                                )}
                            </div>
                          </td>
                          <td>
                            <div className="markscheme-body-preview">
                              {createHTMLPreview(item.markscheme_body, 200)}
                              {item.markscheme_body &&
                                item.markscheme_body.length > 200 && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleEdit(item)}
                                    className="p-0 ms-1"
                                  >
                                    View Full Mark Scheme
                                  </Button>
                                )}
                            </div>
                          </td>
                          {(canEdit || canDelete) && (
                            <td className="text-center">
                              {canEdit && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleEdit(item)}
                                >
                                  Edit
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(item)}
                                >
                                  Delete
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-4">
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(totalPages).keys()]
                .slice(
                  Math.max(0, currentPage - 3),
                  Math.min(totalPages, currentPage + 2)
                )
                .map((number) => (
                  <Pagination.Item
                    key={number + 1}
                    active={number + 1 === currentPage}
                    onClick={() => paginate(number + 1)}
                  >
                    {number + 1}
                  </Pagination.Item>
                ))}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}

          {totalItems > 0 && (
            <p className="text-center text-muted mt-2">
              Showing {data.length} of {totalItems} questions
            </p>
          )}
        </>
      ) : (
        showRumbleSearch && (
          <Card className="text-center p-4 mt-4">
            <Card.Body>
              <Card.Title>No Data Displayed Yet</Card.Title>
              <Card.Text>
                {!selectedSubject
                  ? "Please select a subject to begin."
                  : !selectedLevel
                  ? "Now select a level to continue."
                  : !topicInput
                  ? "Enter a topic to search for specific questions."
                  : "Click the Search button to display questions."}
              </Card.Text>
            </Card.Body>
          </Card>
        )
      )}

      {/* View/Edit Modal with HTML rendering */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {canEdit ? "Edit Question" : "View Question"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canEdit ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={currentRecord.title || ""}
                  onChange={handleInputChange}
                  readOnly={!canEdit}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Question</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="question_body"
                  value={currentRecord.question_body || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Mark Scheme</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="markscheme_body"
                  value={currentRecord.markscheme_body || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <>
                <hr />
                <details>
                  <summary className="mb-2">Additional Details</summary>
                  <Form.Group className="mb-3">
                    <Form.Label>Level</Form.Label>
                    <Form.Control
                      type="text"
                      name="level"
                      value={currentRecord.level || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={currentRecord.subject || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Marks</Form.Label>
                    <Form.Control
                      type="text"
                      name="marks"
                      value={currentRecord.marks || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Reference Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="reference_code"
                      value={currentRecord.reference_code || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </details>
              </>
            </Form>
          ) : (
            renderQuestion(currentRecord)
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          {canEdit && (
            <Button variant="primary" onClick={handleSubmit}>
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Add Toast component for flag notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showFlagToast}
          onClose={() => setShowFlagToast(false)}
          delay={3000}
          autohide
          bg={flagToastType === "success" ? "success" : "danger"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {flagToastType === "success" ? "Success" : "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body
            className={flagToastType === "success" ? "text-white" : ""}
          >
            {flagToastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Latest Worksheets Carousel - Always at bottom */}
      <LatestWorksheetsCarousel />
    </div>
  );
};

export default DataList;
